import { useState, useCallback, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import {
  createCashfreePaymentLink,
  createManualPayment,
  getPaymentGateways,
  getPaymentCatalog,
  getApplicationForms,
} from 'services/paymentServices';
import { getIntegrationsByType } from 'services/integrationService';

export const usePaymentLinkModal = (leadId, refreshProgressJourney, refreshPaymentLinks, isFeePayment = false, isInternational = false, refreshLeadData) => {
  const [isOpen, setIsOpen] = useState(false);
  const [paymentLinkStep, setPaymentLinkStep] = useState('gateway');

  const [gateways, setGateways] = useState([]);
  const [isLoadingGateways, setIsLoadingGateways] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);

  const [paymentCatalogs, setPaymentCatalogs] = useState([]);
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState(null);

  const [applicationForms, setApplicationForms] = useState([]);
  const [isLoadingApplicationForms, setIsLoadingApplicationForms] = useState(false);
  const [selectedApplicationForm, setSelectedApplicationForm] = useState(null);

  const [paymentFormData, setPaymentFormData] = useState({
    amount: '',
    currency: isInternational ? 'USD' : 'INR',
    description: '',
    applicationFormId: null,
    paymentMethod: '',
    transactionId: '',
    collegeId: null,
    courseId: null,
    collegeName: '',
    courseName: '',
  });

  const [isCreatingLink, setIsCreatingLink] = useState(false);
  const [createdLinkData, setCreatedLinkData] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const [feeBreakup, setFeeBreakup] = useState({ 
    academic: {
      college: '',
      course: '',
      isScholarshipApplied: false,
      amount: 0,
      feeDetails: null
    }, 
    hostel: {
      selectedCell: null,
      amount: 0
    }
  });

  // Update currency if isInternational changes
  useEffect(() => {
    setPaymentFormData(prev => ({
      ...prev,
      currency: isInternational ? 'USD' : 'INR'
    }));
  }, [isInternational]);

  // Load payment gateways on mount
  useEffect(() => {
    const loadPaymentGateways = async () => {
      setIsLoadingGateways(true);
      try {
        // Fetch integrations of type PAYMENT
        const response = await getIntegrationsByType('PAYMENT');

        if (response.success) {
          const integrations = Array.isArray(response.data) ? response.data : [];

          // Map integrations to gateway format expected by UI
          // Assuming IntegrationItem has ProviderName, ProviderCode, IntegrationProductId (or IntegrationProviderId)
          // We map them to GatewayName, GatewayCode, GatewayId
          const mappedGateways = integrations.map(item => ({
            GatewayId: item.IntegrationProductId || item.IntegrationProviderId, // Use ProductId or ProviderId as identifier
            GatewayName: item.ProviderName,
            GatewayCode: item.ProviderCode,
            // specific fields if needed
            ...item
          }));

          // Ensure 'Manual' gateway is present if needed, or if it comes from API
          // If Manual is not in integrations, we might need to append it or backend includes it.
          // For now, assuming backend returns all available payment options including manual if configured.

          setGateways(mappedGateways);
        } else {
          toast.error(response.error || 'Failed to load payment options');
          setGateways([]);
        }
      } catch (error) {
        console.error('Error loading payment options:', error);
        toast.error('Failed to load payment options');
        setGateways([]);
      } finally {
        setIsLoadingGateways(false);
      }
    };

    loadPaymentGateways();
  }, []);

  const loadPaymentCatalogs = useCallback(async () => {
    setIsLoadingCatalogs(true);
    try {
      const response = await getPaymentCatalog();
      if (response.success) {
        setPaymentCatalogs(Array.isArray(response.data) ? response.data : []);
        await refreshProgressJourney();
      } else {
        toast.error(response.message || 'Failed to load payment catalogs');
        setPaymentCatalogs([]);
      }
    } catch (error) {
      console.error('Error loading payment catalogs:', error);
      toast.error('Failed to load payment catalogs');
      setPaymentCatalogs([]);
    } finally {
      setIsLoadingCatalogs(false);
    }
  }, []);

  const loadApplicationForms = useCallback(async () => {
    setIsLoadingApplicationForms(true);
    try {
      const response = await getApplicationForms();
      if (response.success) {
        setApplicationForms(Array.isArray(response.data) ? response.data : []);
      } else {
        toast.error(response.message || 'Failed to load application forms');
        setApplicationForms([]);
      }
    } catch (error) {
      console.error('Error loading application forms:', error);
      toast.error('Failed to load application forms');
      setApplicationForms([]);
    } finally {
      setIsLoadingApplicationForms(false);
    }
  }, []);

  // Load catalogs and forms when modal opens
  useEffect(() => {
    if (!isOpen) return;
    if (paymentCatalogs.length === 0 && !isLoadingCatalogs) {
      loadPaymentCatalogs();
    }
    if (applicationForms.length === 0 && !isLoadingApplicationForms) {
      loadApplicationForms();
    }
  }, [isOpen, paymentCatalogs.length, applicationForms.length, isLoadingCatalogs, isLoadingApplicationForms, loadPaymentCatalogs, loadApplicationForms]);

  const handleBulkUpdate = useCallback((newData) => {
    setPaymentFormData(prev => ({
      ...prev,
      ...newData,
    }));
  }, []);

  const resetState = useCallback(() => {
    setPaymentFormData({
      amount: '',
      currency: isInternational ? 'USD' : 'INR',
      description: '',
      applicationFormId: null,
      paymentMethod: '',
      transactionId: '',
      collegeId: null,
      courseId: null,
      collegeName: '',
      courseName: '',
    });
    setSelectedGateway(null);
    setSelectedCatalog(null);
    setSelectedApplicationForm(null);
    setIsCreatingLink(false);
    setCreatedLinkData(null);
    setCopySuccess(false);
    setPaymentLinkStep('gateway');
    setFeeBreakup({ 
      academic: {
        college: '',
        course: '',
        isScholarshipApplied: false,
        amount: 0,
        feeDetails: null
      }, 
      hostel: {
        selectedCell: null,
        amount: 0
      }
    });
  }, [isInternational]);

  const handleOpen = useCallback(() => {
    resetState();
    setIsOpen(true);
  }, [resetState]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    resetState();
  }, [resetState]);

  const handleSelectGateway = useCallback((gateway) => {
    setSelectedGateway(gateway);
    setSelectedCatalog(null);
    setPaymentFormData({
      amount: '',
      currency: isInternational ? 'USD' : 'INR',
      description: '',
      applicationFormId: null,
      paymentMethod: '',
      transactionId: '',
      collegeId: null,
      courseId: null,
      collegeName: '',
      courseName: '',
    });
    setPaymentLinkStep(isFeePayment ? 'academic_fee' : 'details');
  }, [isFeePayment, isInternational]);

  const handleSelectCatalog = useCallback((option) => {
    const catalog = option?.value;
    if (!catalog) {
      setSelectedCatalog(null);
      setPaymentFormData(prev => ({
        ...prev,
        amount: '',
        currency: isInternational ? 'USD' : 'INR',
      }));
      return;
    }

    setSelectedCatalog(catalog);
    setPaymentFormData(prev => ({
      ...prev,
      amount: isFeePayment && prev.amount ? prev.amount : (catalog.Amount !== undefined && catalog.Amount !== null ? String(catalog.Amount) : prev.amount),
      currency: isFeePayment ? (isInternational ? 'USD' : 'INR') : (catalog.Currency || (isInternational ? 'USD' : 'INR')),
      description: catalog.Description || prev.description || '',
    }));
  }, [isInternational, isFeePayment]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;

    // Prevent negative numbers only for amount field
    const cleanedValue =
      name === "amount" ? value.replace(/-/g, "") : value;

    setPaymentFormData(prev => ({
      ...prev,
      [name]: cleanedValue,
    }));
  }, []);


  const handleCreatePaymentLink = useCallback(async () => {
    if (!selectedCatalog || !paymentFormData.amount || !selectedGateway || !paymentFormData.description || !selectedApplicationForm) {
      toast.error('Please fill out all required fields including application form selection.');
      return;
    }

    setIsCreatingLink(true);

    try {
      const payload = {
        leadId: leadId,
        amount: parseFloat(paymentFormData.amount),
        currency: paymentFormData.currency,
        description: paymentFormData.description,
        applicationFormId: selectedApplicationForm.FormId,
        isEnrollmentFee: isFeePayment,
      };

      if (!isFeePayment) {
        payload.applicationFee = parseFloat(paymentFormData.amount);
      } else {
        payload.courseFee = parseFloat(feeBreakup.academic?.amount || 0);
        payload.hostelFee = parseFloat(feeBreakup.hostel?.amount || 0);
        payload.isScolarShip = feeBreakup.academic?.isScholarshipApplied || false;
        payload.ptuCounselingFee = parseFloat(feeBreakup.academic?.ptuFee || 0);
        
        if (feeBreakup.hostel?.hostelRatesId) {
          payload.hostelRatesId = feeBreakup.hostel.hostelRatesId;
        }
      }

      // Add manual payment specific fields if it's a manual gateway
      if (selectedGateway.GatewayCode === 'MAN') {
        payload.paymentMethod = paymentFormData.paymentMethod || '';
        payload.transactionId = paymentFormData.transactionId || '';
        payload.collegeName = paymentFormData.collegeName || '';
        payload.courseName = paymentFormData.courseName || '';
      }

      // Use different API based on gateway type
      let response;
      if (selectedGateway.GatewayCode === 'MAN') {
        const manualPayload = {
          ...payload,
          IntegrationProductId: selectedGateway.GatewayId
        };
        manualPayload.paymentMethod = paymentFormData.paymentMethod || '';
        manualPayload.transactionId = paymentFormData.transactionId || '';

        response = await createManualPayment(manualPayload);
      } else {
        const cashfreePayload = {
          ...payload,
          IntegrationProductId: selectedGateway.GatewayId,
          Provider: selectedGateway.GatewayCode
        };
        response = await createCashfreePaymentLink(cashfreePayload);
      }

      if (response.success) {
        setCreatedLinkData(response.data);
        setPaymentLinkStep('success');
        await refreshProgressJourney();
        if (refreshLeadData) await refreshLeadData(true);

        // Show different success message for manual payment
        if (selectedGateway.GatewayCode === 'MAN') {
          toast.success('Manual payment recorded successfully');
        } else {
          toast.success('Payment link created successfully');
        }

        refreshPaymentLinks();
      } else {
        toast.error(response.error || 'Failed to create payment link');
      }
    } catch (error) {
      console.error('Error creating payment link:', error);
      toast.error('Failed to create payment link. Please try again.');
    } finally {
      setIsCreatingLink(false);
    }
  }, [leadId, selectedGateway, selectedCatalog, selectedApplicationForm, paymentFormData, refreshProgressJourney, refreshPaymentLinks]);

  const handleCopyPaymentLink = useCallback(async () => {
    if (!createdLinkData?.LinkUrl) return;

    try {
      await navigator.clipboard.writeText(createdLinkData.LinkUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
      const textArea = document.createElement('textarea');
      textArea.value = createdLinkData.LinkUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  }, [createdLinkData]);

  const activePaymentCatalogs = useMemo(
    () => paymentCatalogs.filter((catalog) => catalog?.IsActive),
    [paymentCatalogs]
  );

  return {
    isOpen,
    paymentLinkStep,
    gateways,
    isLoadingGateways,
    selectedGateway,
    paymentCatalogs: activePaymentCatalogs,
    isLoadingCatalogs,
    selectedCatalog,
    applicationForms,
    isLoadingApplicationForms,
    selectedApplicationForm,
    paymentFormData,
    isCreatingLink,
    createdLinkData,
    copySuccess,
    handleOpen,
    handleClose,
    handleSelectGateway,
    handleSelectCatalog,
    handleInputChange,
    handleCreatePaymentLink,
    handleCopyPaymentLink,
    setPaymentLinkStep,
    setSelectedApplicationForm,
    resetState,
    handleBulkUpdate,
    feeBreakup,
    setFeeBreakup,
  };
};
